// ============================================
// useCTraderMasterPositions
//
// The open positions of the cTrader MASTER, both books, live on topic
// ctrader.position, followed by the four rules of the Node Data API (the same
// rules the B-Book page follows):
//   SNAPSHOT replaces everything, POSITION_SYNC replaces one node,
//   ADD / CHANGE insert or replace the row (node_id, position_id),
//   DELETE removes it, POSITION_PRICES sets price and profit of rows held.
// The topic carries every started node (STANDBY, CLIENT and PARTNER too);
// only the MASTER's rows are handed to the page. Nothing is polled: the
// MASTER's list route is read once for a first paint, before the SNAPSHOT.
//
// A page that already holds a socket on /ws/v1/mt5/events passes its frames
// to `handle` and subscribes to the topic itself; otherwise `ownSocket`
// opens one. That socket also carries the MT5 position stream (frames of
// about 1 MB), so anything that is not a cTrader position frame is dropped
// before it is parsed.
// ============================================

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ctraderDataApi,
  CTRADER_POSITION_TOPIC,
  type CTraderPositionMessage,
  type CTraderPositionRow,
} from '@/services/api';
import type { CTraderMasterState } from '@/hooks/usePlatformMasters';

const WS_BASE = (import.meta as any).env?.VITE_WS_URL || 'ws://localhost:8080';

export interface CTraderMasterPositions {
  /** The MASTER's open positions, both books, as the topic sends them (client side). */
  rows:  CTraderPositionRow[];
  /** A SNAPSHOT (or the first-paint read) has arrived. */
  known: boolean;
  /** Feed one frame of topic ctrader.position. Only for pages without ownSocket. */
  handle: (msg: CTraderPositionMessage) => void;
}

export function useCTraderMasterPositions(
  master: CTraderMasterState | null | undefined,
  opts: { ownSocket?: boolean } = {},
): CTraderMasterPositions {
  const storeRef  = useRef<Map<number, Map<number, CTraderPositionRow>>>(new Map());
  const knownRef  = useRef(false);
  const masterRef = useRef(master);
  masterRef.current = master;

  const [rows, setRows]   = useState<CTraderPositionRow[]>([]);
  const [known, setKnown] = useState(false);

  // Rows handed to the page: the MASTER's only. Coalesced to one React update
  // per animation frame — POSITION_PRICES arrives up to once a second per node.
  const pendingRef = useRef(false);
  const publish = useCallback(() => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    requestAnimationFrame(() => {
      pendingRef.current = false;
      const m = masterRef.current;
      const node = m ? storeRef.current.get(m.id) : undefined;
      setRows(node ? [...node.values()] : []);
    });
  }, []);

  const handle = useCallback((msg: CTraderPositionMessage) => {
    const store = storeRef.current;
    if (msg.type === 'SNAPSHOT') {
      store.clear();
      for (const r of msg.data.positions ?? []) {
        if (!store.has(r.node_id)) store.set(r.node_id, new Map());
        store.get(r.node_id)!.set(r.position_id, r);
      }
      if (!knownRef.current) { knownRef.current = true; setKnown(true); }
      publish();
      return;
    }
    if (msg.type === 'POSITION_SYNC') {
      const m = new Map<number, CTraderPositionRow>();
      for (const r of msg.data.positions ?? []) m.set(r.position_id, r);
      store.set(msg.data.node_id, m);
      if (masterRef.current?.id === msg.data.node_id) publish();
      return;
    }
    if (msg.type === 'POSITION_PRICES') {
      const node = store.get(msg.data.node_id);
      if (!node) return;
      for (const e of msg.data.prices ?? []) {
        const held = node.get(e.position_id);
        if (!held) continue;
        node.set(e.position_id, {
          ...held,
          price_current: e.price_current,
          profit: e.profit,
          ...(e.profit_usd !== undefined && { profit_usd: e.profit_usd }),
          ...(e.usd_rate !== undefined && { usd_rate: e.usd_rate }),
        });
      }
      if (masterRef.current?.id === msg.data.node_id) publish();
      return;
    }
    const r = msg.data;
    if (!r) return;
    if (!store.has(r.node_id)) store.set(r.node_id, new Map());
    const node = store.get(r.node_id)!;
    if (msg.type === 'POSITION_DELETE') node.delete(r.position_id);
    else node.set(r.position_id, r);
    if (masterRef.current?.id === r.node_id) publish();
  }, [publish]);

  // A change of MASTER (a takeover) only changes which node is handed out.
  useEffect(() => { publish(); }, [master?.id, publish]);

  // First paint from the MASTER's list route, both books, before the SNAPSHOT.
  useEffect(() => {
    if (!master || knownRef.current) return;
    let cancelled = false;
    (async () => {
      const got: CTraderPositionRow[] = [];
      try {
        for (let offset = 0; ;) {
          const res = await ctraderDataApi.getPositions(master.id, { limit: 1000, offset });
          if (cancelled || knownRef.current) return;
          for (const p of res.positions ?? []) got.push({ ...p, platform: 'CTRADER', node_id: master.id, node_name: master.name });
          offset += res.returned;
          if (!res.returned || offset >= res.total) break;
        }
      } catch { return; }   // 503: the node is not started; the socket will say so
      if (cancelled || knownRef.current) return;
      const m = new Map<number, CTraderPositionRow>();
      for (const r of got) m.set(r.position_id, r);
      storeRef.current.set(master.id, m);
      publish();
    })();
    return () => { cancelled = true; };
  }, [master?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Own socket, with the B-Book page's back-off (1 s → 2 s → 4 s … 30 s).
  useEffect(() => {
    if (!opts.ownSocket) return;
    let ws: WebSocket | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let destroyed = false;
    let attempt = 0;
    const connect = () => {
      if (destroyed) return;
      ws = new WebSocket(`${WS_BASE}/ws/v1/mt5/events`);
      ws.onopen = () => {
        attempt = 0;
        // A reconnect gets a new SNAPSHOT, which replaces everything.
        ws?.send(JSON.stringify({ type: 'subscribe', topics: [CTRADER_POSITION_TOPIC] }));
      };
      ws.onmessage = (ev: MessageEvent<string>) => {
        if (typeof ev.data !== 'string' || !ev.data.includes(CTRADER_POSITION_TOPIC)) return;
        try {
          const msg = JSON.parse(ev.data);
          if (msg.topic === CTRADER_POSITION_TOPIC) handle(msg as CTraderPositionMessage);
        } catch { /* malformed */ }
      };
      ws.onclose = () => {
        if (destroyed) return;
        const wait = Math.min(1000 * 2 ** attempt, 30_000);
        attempt += 1;
        timer = setTimeout(connect, wait);
      };
    };
    connect();
    return () => {
      destroyed = true;
      if (timer) clearTimeout(timer);
      if (ws) { ws.onclose = null; ws.close(1000, 'Client disconnecting'); }
    };
  }, [opts.ownSocket, handle]);

  return { rows, known, handle };
}

/** account currency × usd_rate = USD; before Data API 1.4 a USD account needs no rate. */
export function ctUsdRate(r: CTraderPositionRow): number | null {
  if (r.usd_rate !== undefined) return r.usd_rate;
  return r.currency === 'USD' ? 1 : null;
}

/** Floating profit in USD, client side. null without a price or a rate to USD. */
export function ctProfitUsd(r: CTraderPositionRow): number | null {
  if (r.profit_usd !== undefined) return r.profit_usd;
  const rate = ctUsdRate(r);
  return r.profit !== null && rate !== null ? Math.round(r.profit * rate * 100) / 100 : null;
}
